import { byAttrVal, createCustomElement, DOMModel, registerEvent } from '@adobe/react-webcomponent'
import { ModalContextProvider } from '@billy/design-system'
import type { ComponentProps } from 'react'
import React, { ReactElement } from 'react'
import { QueryClientProvider } from 'react-query'
import { Provider as ReduxProvider } from 'react-redux'
import { unstable_HistoryRouter as HistoryRouter } from 'react-router'

import { store } from '@modules-deprecated'
import { AccountsContextProvider } from '@modules-deprecated/app/accounts'
import {
  UserOrganizationContextProvider,
  UserOrganizationSettingsContextProvider,
} from '@modules-deprecated/app/organization'
import { UmbrellaContextProvider } from '@modules-deprecated/app/umbrellas'
import { CurrentUserContextProvider } from '@modules-deprecated/app/user'
import { UserNotificationSettingsContextProvider } from '@modules-deprecated/app/user/userNotificationSettingsContext'
import { Umbrella as UmbrellaComponent } from '@views/umbrella/Umbrella'

import { queryClient } from '../../config/queryClient'
import { DirtyRouteContextProvider, DirtyRouteProps } from '../../contexts/dirtyRouteContext'
import { EmberNavigate, EmberRouterContextProvider } from '../../contexts/emberRouterContext'
import { InboxOrganizationIdContextProvider } from '../../contexts/inboxOrganizationIdContext'
import { ThemeProvider } from '../../contexts/themeProvider'
import { useHashHistory } from '../../hooks/routing/useHashHistory'

// history's HashHistory isn't exactly @remix-run/router's History type (extra methods unused at
// runtime) - same cast HistoryRouter.tsx already uses for the outer shared history singleton.
type CompatHistory = ComponentProps<typeof HistoryRouter>['history']

const MODULE_NAME = 'umbrella-module'

class UmbrellaModuleWebComponent extends DOMModel {
  @byAttrVal organizationid: string
  @byAttrVal umbrellaId: string
  @registerEvent('navigate') navigate: Function
  @registerEvent('leaveRoute') leaveRoute: Function
  @registerEvent('setDirtyRouteFlag') setDirtyRouteFlag: Function
}

interface UmbrellaModuleProps extends DirtyRouteProps {
  onNavigate: EmberNavigate
  organizationid: string
  umbrellaId: string
}

export const UmbrellaModule = ({
  onLeaveRoute,
  onNavigate,
  onSetDirtyRouteFlag,
  organizationid,
  umbrellaId,
}: UmbrellaModuleProps): ReactElement => {
  const hashHistory = useHashHistory()

  return (
    <ThemeProvider>
      <HistoryRouter history={hashHistory as unknown as CompatHistory}>
        <QueryClientProvider client={queryClient}>
          <EmberRouterContextProvider onNavigate={onNavigate}>
            <UmbrellaContextProvider umbrellaId={umbrellaId}>
              <CurrentUserContextProvider>
                <UserOrganizationContextProvider organizationId={organizationid}>
                  <UserOrganizationSettingsContextProvider organizationId={organizationid}>
                    <AccountsContextProvider organizationId={organizationid}>
                      <ModalContextProvider>
                        <DirtyRouteContextProvider
                          hashHistory={hashHistory}
                          onLeaveRoute={onLeaveRoute}
                          onSetDirtyRouteFlag={onSetDirtyRouteFlag}
                        >
                          <UserNotificationSettingsContextProvider>
                            <InboxOrganizationIdContextProvider>
                              <ReduxProvider store={store}>
                                <UmbrellaComponent />
                              </ReduxProvider>
                            </InboxOrganizationIdContextProvider>
                          </UserNotificationSettingsContextProvider>
                        </DirtyRouteContextProvider>
                      </ModalContextProvider>
                    </AccountsContextProvider>
                  </UserOrganizationSettingsContextProvider>
                </UserOrganizationContextProvider>
              </CurrentUserContextProvider>
            </UmbrellaContextProvider>
          </EmberRouterContextProvider>
        </QueryClientProvider>
      </HistoryRouter>
    </ThemeProvider>
  )
}

const UmbrellaModuleCustomElement = createCustomElement(UmbrellaModule, UmbrellaModuleWebComponent, 'element')

customElements.get(MODULE_NAME) || customElements.define(MODULE_NAME, UmbrellaModuleCustomElement)

export default UmbrellaModuleCustomElement
